import React, { useEffect, useCallback, useMemo, useContext } from "react";
import { ActiveEditorContext } from "../context/ActiveEditorContext";
import TablePicker from "./TablePicker";
import StarCircleButton from "./StarCircleButton";
import AIStarAssistantModal from "./AIStarAssistantModal";
import SmartLessonPanel from "./AddExerciseSmartLesson/SmartLessonPanel";
import AskMePanel from "./AddExerciseSmartLesson/AskMePanel";
import GeneratorPanelsContainer from "./AddExerciseSmartLesson/GeneratorPanelsContainer";
import PDFPageSelectorModal from './PDFPageSelectorModal';
import MaterialsSelectorModal from './MaterialsSelectorModal';
import { fetchOpenAIText } from '../utils/openaiClient';
import { useExerciseGenerators } from '../hooks/useExerciseGenerators';
import { useAskMeState } from '../hooks/useAskMeState';
import { useUnsplashState } from '../hooks/useUnsplashState';
import { useTemplateState } from '../hooks/useTemplateState';
import { useMenuVisibility } from '../hooks/useMenuVisibility';

// API key is now stored securely on the server (Vercel Serverless Function)



// Функция для обработки прокрутки в выпадающих списках
const handleScrollInDropdown = (e) => {
  // Let the browser handle scrolling natively (fastest)
  e.stopPropagation();
  // Do NOT preventDefault and do NOT manually set scrollTop
};

// YouTube URL parsing function
function extractYouTubeId(url) {
  const patterns = [
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/,
    /youtube\.com\/embed\/([^"&?/\s]{11})/,
    /youtube\.com\/v\/([^"&?/\s]{11})/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

const icons = {
  h1: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="3" height="12" rx="1" fill="currentColor"/><rect x="15" y="4" width="3" height="12" rx="1" fill="currentColor"/><rect x="6" y="9" width="8" height="2" rx="1" fill="currentColor"/></svg>
  ),
  h2: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="3" height="12" rx="1" fill="currentColor"/><rect x="15" y="4" width="3" height="12" rx="1" fill="currentColor"/><rect x="6" y="9" width="8" height="2" rx="1" fill="currentColor"/><rect x="6" y="13" width="8" height="2" rx="1" fill="currentColor"/></svg>
  ),
  table: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="5" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 10h14" stroke="currentColor" strokeWidth="1.5"/><path d="M8.5 5v10" stroke="currentColor" strokeWidth="1.5"/></svg>
  ),
  divider: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="4" y="9" width="12" height="2" rx="1" fill="currentColor"/></svg>
  ),
  pagebreak: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="4" y="4" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M4 12h12" stroke="currentColor" strokeWidth="1.5"/></svg>
  ),
  checklist: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="4" y="4" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M7 10l2 2 4-4" stroke="currentColor" strokeWidth="1.5"/></svg>
  ),
  bullet: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="6" cy="10" r="2" fill="currentColor"/><rect x="10" y="9" width="6" height="2" rx="1" fill="currentColor"/></svg>
  ),
  number: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><text x="4" y="15" fontSize="10" fill="currentColor">1.</text><rect x="10" y="9" width="6" height="2" rx="1" fill="currentColor"/></svg>
  ),
  image: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="5" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/><circle cx="7" cy="9" r="1.5" fill="currentColor"/><path d="M3 15l4-4a2 2 0 0 1 2.8 0l3.2 3.2" stroke="currentColor" strokeWidth="1.5"/></svg>
  ),
  link: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7 13a5 5 0 0 1 0-7l2-2a5 5 0 0 1 7 7l-1 1" stroke="currentColor" strokeWidth="1.5"/><path d="M13 7a5 5 0 0 1 0 7l-2 2a5 5 0 0 1-7-7l1-1" stroke="currentColor" strokeWidth="1.5"/></svg>
  ),
  youtube: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="16" height="10" rx="3" stroke="currentColor" strokeWidth="1.5"/><polygon points="9,8 14,10 9,12" fill="currentColor"/></svg>
  ),
  file: (
    <svg width="20" height="20" viewBox="0 0 22.18 29.25" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <g>
        <path d="m14.79 19.55h-7.4c-.34 0-.62.27-.62.61s.28.62.62.62h7.4c.34 0 .62-.28.62-.62s-.28-.61-.62-.61zm0 2.46h-7.4c-.34 0-.62.28-.62.62s.28.62.62.62h7.4c.34 0 .62-.28.62-.62s-.28-.62-.62-.62zm-3.76-7.4h-3.64c-.34 0-.62.28-.62.62s.28.61.62.61h3.64c.34 0 .62-.27.62-.61s-.28-.62-.62-.62zm3.76 2.47h-7.4c-.34 0-.62.27-.62.61 0 .35.28.62.62.62h7.4c.34 0 .62-.27.62-.62 0-.34-.28-.61-.62-.61z"/>
        <path d="m4.24 0h9.65l8.29 8.65v16.36c0 1.17-.47 2.23-1.24 3s-1.83 1.24-3 1.24h-13.7c-1.17 0-2.23-.47-3-1.24-.76-.77-1.24-1.83-1.24-3v-20.77c0-1.17.48-2.23 1.24-3 .77-.76 1.83-1.24 3-1.24zm9.05 1.19v8.02h7.7v15.8c0 .84-.34 1.61-.89 2.16-.56.55-1.32.9-2.16.9h-13.7c-.84 0-1.6-.35-2.16-.9-.55-.55-.89-1.32-.89-2.16v-20.77c0-.84.34-1.6.89-2.16.56-.55 1.32-.89 2.16-.89h9.05z"/>
      </g>
    </svg>
  ),
  quiz: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="4" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><circle cx="7" cy="9" r="1" fill="currentColor"/><circle cx="13" cy="9" r="1" fill="currentColor"/><circle cx="7" cy="13" r="1" fill="currentColor"/><circle cx="13" cy="13" r="1" fill="currentColor"/><path d="M9 7h2" stroke="currentColor" strokeWidth="1.5"/><path d="M9 11h2" stroke="currentColor" strokeWidth="1.5"/></svg>
  ),
  lightbulb: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10 2C7.24 2 5 4.24 5 7c0 2.85 1.92 4.63 3.39 6.12.37.38.61.89.61 1.43V16c0 .55.45 1 1 1s1-.45 1-1v-1.45c0-.54.24-1.05.61-1.43C13.08 11.63 15 9.85 15 7c0-2.76-2.24-5-5-5z"/>
      <path d="M8 17h4"/>
      <path d="M9 18h2"/>
    </svg>
  ),
  grammarRules: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
    </svg>
  ),
  columnsGap: (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
      <path d="M6 1v3H1V1zM1 0a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1zm14 12v3h-5v-3zm-5-1a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1zM6 8v7H1V8zM1 7a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1zm14-6v7h-5V1zm-5-1a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1z"/>
    </svg>
  ),
  bookmarkStar: (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
      <path d="M7.84 4.1a.178.178 0 0 1 .32 0l.634 1.285a.18.18 0 0 0 .134.098l1.42.206c.145.021.204.2.098.303L9.42 6.993a.18.18 0 0 0-.051.158l.242 1.414a.178.178 0 0 1-.258.187l-1.27-.668a.18.18 0 0 0-.165 0l-1.27.668a.178.178 0 0 1-.257-.187l.242-1.414a.18.18 0 0 0-.05-.158l-1.03-1.001a.178.178 0 0 1 .098-.303l1.42-.206a.18.18 0 0 0 .134-.098z"/>
      <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z"/>
    </svg>
  ),
  chatBubble: (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
      <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
      <circle cx="5" cy="6" r="1"/>
      <circle cx="8" cy="6" r="1"/>
      <circle cx="11" cy="6" r="1"/>
    </svg>
  ),
  fire: (
    <svg width="20" height="20" viewBox="0 0 135.2716 291.7073" fill="currentColor">
      <path d="m87.7896 53.271c13.1049 0 23.9709 10.2576 24.7375 23.3508.3564 6.0999-1.6229 11.7529-5.0409 16.2891.8575 2.1729 1.6064 4.1699 2.251 5.9961-9.3091 3.0762-20.5884 7.6074-24.3567 9.1479-.6042-1.1011-1.1774-2.1655-1.853-3.3386-11.5522-20.0562-33.6853-50.3088-71.3037-73.6877-3.7531-2.3315-8.6859-1.1804-11.0175 2.5732-2.3322 3.7524-1.1804 8.6853 2.572 11.0168 33.3844 20.7471 53.6615 47.771 64.6124 66.1646 3.205 5.3821 5.6067 9.9792 7.2937 13.4949.0513.1648.0134.332.0714.4956 1.001 2.8223 23.8037 69.9512-39.6094 144.6387-4.4727 5.2539-3.8281 13.1445 1.4355 17.6172 2.3486 1.9922 5.2246 2.9688 8.0859 2.9688 3.54 0 7.0605-1.4941 9.5312-4.4141 28.0048-32.9773 41.1841-64.9902 46.7474-92.1619l7.5092-.4358c-1.3733 23.7769-5.1587 51.8213-13.4949 82.9883-1.7822 6.6699 2.1777 13.5254 8.8428 15.3125 1.084.2832 2.1729.4199 3.2422.4199 5.5176 0 10.5713-3.6816 12.0654-9.2676 26.7462-99.9854 10.2673-169.5508 8.7946-175.3638-.0018-.0098.0012-.0171-.0006-.0269-.5225-2.4219-13.4326-59.9316-68.2275-105.2051-3.418-2.832-8.4521-2.3438-11.2646 1.0645s-2.334 8.4473 1.0693 11.2598c15.1532 12.5244 26.8073 26.1694 35.7751 39.1553.0214 0 .0409-.0073.0623-.0098.4933-.0293.9822-.0427 1.4699-.0427z"/>
      <path d="m89.0829 100.533c12.4042-.7251 21.8732-11.3696 21.1481-23.7769-.7263-12.4048-11.3727-21.8726-23.7769-21.1462-10.8612.636-19.4678 8.8721-20.9564 19.2371 7.4628 9.0857 13.3459 17.6855 17.8815 25.21 1.845.3685 3.7377.5907 5.7037.476z"/>
    </svg>
  ),
  trueFalse: (
    <svg width="20" height="20" viewBox="0 0 34 34" fill="currentColor">
      <g>
        <g>
          <path d="m30 15h-6v1c0 2.8-2.2 5-5 5h-7v8c0 1.7 1.3 3 3 3h15c1.7 0 3-1.3 3-3v-11c0-1.7-1.3-3-3-3zm-2.3 6.7-6 6c-.2.2-.4.3-.7.3s-.5-.1-.7-.3l-3-3c-.4-.4-.4-1 0-1.4s1-.4 1.4 0l2.3 2.3 5.3-5.3c.4-.4 1-.4 1.4 0s.4 1 0 1.4z"/>
        </g>
        <g>
          <path d="m19 2h-15c-1.7 0-3 1.3-3 3v11c0 1.7 1.3 3 3 3h15c1.7 0 3-1.3 3-3v-11c0-1.7-1.3-3-3-3zm-3.4 11.2c.4.4.4 1 0 1.4s-1 .4-1.4 0l-2.7-2.7-2.7 2.7c-.4.4-1 .4-1.4 0s-.4-1 0-1.4l2.7-2.7-2.7-2.7c-.4-.4-.4-1 0-1.4s1-.4 1.4 0l2.7 2.7 2.7-2.7c.4-.4 1-.4 1.4 0s.4 1 0 1.4l-2.7 2.7z"/>
        </g>
      </g>
    </svg>
  ),
  balance: (
    <svg width="20" height="20" viewBox="0 0 682.66669 682.66669" fill="currentColor">
      <g transform="matrix(1.3333333,0,0,-1.3333333,0,682.66667)">
        <g>
          <g clipPath="url(#clipPath3632)">
            <g transform="translate(327,255)">
              <path d="m 0,0 h 140 c 16.568,0 30,-13.432 30,-30 v -180 c 0,-16.568 -13.432,-30 -30,-30 H 0 c -16.568,0 -30,13.432 -30,30 v 180 c 0,16.568 13.432,30 30,30 z" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(45,255)">
              <path d="m 0,0 h 140 c 16.568,0 30,-13.432 30,-30 v -180 c 0,-16.568 -13.432,-30 -30,-30 H 0 c -16.568,0 -30,13.432 -30,30 v 180 c 0,16.568 13.432,30 30,30 z" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(115,497)">
              <path d="m 0,0 v 0 c 49.706,0 90,-40.294 90,-90 0,-49.706 -40.294,-90 -90,-90 -49.706,0 -90,40.294 -90,90 0,49.706 40.294,90 90,90 z" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(397,497)">
              <path d="m 0,0 v 0 c 49.706,0 90,-40.294 90,-90 0,-49.706 -40.294,-90 -90,-90 -49.706,0 -90,40.294 -90,90 0,49.706 40.294,90 90,90 z" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(75,75)">
              <path d="M 0,0 H 80" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(75,135)">
              <path d="M 0,0 H 80" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(75,195)">
              <path d="M 0,0 H 80" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(437,75)">
              <path d="M 0,0 H -80" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(437,135)">
              <path d="M 0,0 H -80" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(437,195)">
              <path d="M 0,0 H -80" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(115,255)">
              <path d="M 0,0 V 62" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(397,255)">
              <path d="M 0,0 V 62" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(146.8198,428.2129)">
              <path d="M 0,0 -42.426,-42.426 -63.64,-21.213" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(418.2134,428.2129)">
              <path d="M 0,0 -42.427,-42.426" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
            <g transform="translate(375.7866,428.2129)">
              <path d="M 0,0 42.427,-42.426" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeDasharray="none" strokeOpacity="1"/>
            </g>
          </g>
        </g>
      </g>
    </svg>
  ),
  wordDefinition: (
    <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
      <g>
        <g>
          <path d="M260.995,227.715c-9.623,3.423-19.995-3.716-19.995-14.15V121h-71.455c-12.947,0-19.804,15.351-11.177,25.005
            C163.29,151.498,166,158.603,166,166c0,16.538-13.462,30-30,30c-16.538,0-30-13.462-30-30c0-7.397,2.71-14.502,7.632-19.995
            c8.627-9.654,1.77-25.005-11.177-25.005H45c-24.814,0-45,20.186-45,45v180c0,24.814,20.186,45,45,45h31c0,33.091,26.909,60,60,60
            s60-26.909,60-60h45v-92.563c0-10.296,10.712-17.369,19.995-14.15c18.633,6.636,39.99-8.335,40.005-28.286
            C301,236.021,279.657,221.064,260.995,227.715z"/>
        </g>
      </g>
      <g>
        <g>
          <path d="M467,121h-31c0-33.091-26.909-60-60-60s-60,26.909-60,60h-45v75c33.091,0,60,26.909,60,60s-26.909,60-60,60v75h196
            c24.814,0,45-20.186,45-45V166C512,141.186,491.814,121,467,121z"/>
        </g>
      </g>
    </svg>
  ),
  questionBubble: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="m12 6.0211c-1.1046 0-2 .89858-2 2.00704v1.01742c0 .55422-.44771 1.00354-1 1.00354s-1-.44932-1-1.00354v-1.01742c0-2.21691 1.79086-4.01407 4-4.01407h.1126c2.147 0 3.8874 1.74655 3.8874 3.90103 0 1.03461-.4096 2.02685-1.1386 2.7584l-1.0004 1.004c-.5513.5532-.861 1.3035-.861 2.0859v.2858c0 .5543-.4477 1.0036-1 1.0036s-1-.4493-1-1.0036v-.2858c0-1.3147.5204-2.5755 1.4467-3.5051l1.0005-1.00395c.354-.35519.5528-.83694.5528-1.33925 0-1.04603-.845-1.894-1.8874-1.894z"/>
      <path d="m12 18.0633c.5523 0 1-.4493 1-1.0035s-.4477-1.0035-1-1.0035-1 .4493-1 1.0035.4477 1.0035 1 1.0035z"/>
      <path clip-rule="evenodd" d="m0 3.01055c0-1.66268 1.34315-3.01055 3-3.01055h18c1.6569 0 3 1.34787 3 3.01055v14.04925c0 1.6627-1.3431 3.0105-3 3.0105h-2.7861c-.2308 0-.4545.0802-.6332.2269l-3.681 3.0223c-1.1051.9073-2.6943.9073-3.7994 0l-3.68099-3.0223c-.17866-.1467-.40239-.2269-.63324-.2269h-2.78607c-1.65685 0-3-1.3478-3-3.0105zm3-1.00352c-.55228 0-1 .44929-1 1.00352v14.04925c0 .5542.44772 1.0035 1 1.0035h2.78607c.69253 0 1.36373.2404 1.89972.6805l3.68101 3.0223c.3683.3025.8981.3025 1.2664 0l3.681-3.0223c.536-.4401 1.2072-.6805 1.8997-.6805h2.7861c.5523 0 1-.4493 1-1.0035v-14.04925c0-.55423-.4477-1.00352-1-1.00352z" fill-rule="evenodd"/>
    </svg>
  ),
  abcd: (
    <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
      <g>
        <path d="m0 241h241v-241h-231c-5.523 0-10 4.477-10 10zm55.88-61.75 48.13-126.37c.05-.12.09-.24.14-.35 6.058-14.817 27.053-14.816 33.13-.03.05.14.11.27.16.41l47.7 126.39c2.92 7.75-.99 16.4-8.74 19.33-7.98 2.964-16.474-1.157-19.33-8.74-2.588-6.858-.887-2.353-8.22-21.77h-56.62c-7.376 19.343-5.698 14.938-8.31 21.81-2.95 7.74-11.62 11.63-19.36 8.68s-11.63-11.61-8.68-19.36z"/>
        <path d="m271 0v241h241v-231c0-5.523-4.477-10-10-10zm125.12 199.44c-8.977 0-43.74.15-43.74.15-8.285 0-15-6.726-15-15v-128.18c0-8.29 6.72-15 15-15h37.23c35.677 0 56.764 40.06 36.84 69.43 11.66 9.07 19.17 23.22 19.17 39.1 0 27.29-22.21 49.5-49.5 49.5z"/>
        <path d="m10 512h231v-241h-241v231c0 5.523 4.477 10 10 10zm166.59-186.08c6.86 4.64 8.67 13.97 4.03 20.83-4.649 6.873-13.946 8.668-20.83 4.03-32.257-21.796-76.52 1.283-76.52 40.72 0 42.363 49.706 62.049 74.29 40.16 3.899-3.476 6.501-8.785 14.48-9.14 12.923-.641 20.621 14.579 12.18 24.62-40.696 48.713-130.95 17.344-130.95-55.64 0-63.402 71.16-100.805 123.32-65.58z"/>
        <path d="m271 512h231c5.523 0 10-4.477 10-10v-231h-241zm77.77-197.7h35.23c39.02 0 65.23 31.03 65.23 77.2 0 20.23-5.81 38.96-16.38 52.74-11.69 15.26-28.58 23.85-47.56 24.18-8.983.152-35.255.28-36.27.28-7.893 0-14.494-6.152-14.97-14.09v-.05c-.02-.27-.03-.54-.03-.81 0 0-.25-71.621-.25-124.32 0-8.674 6.948-15.13 15-15.13z"/>
        <path d="m120.67 93.43-17.02 44.69h33.88z"/>
        <path d="m384.77 438.42c23.81-.41 34.46-23.86 34.46-46.92 0-22.82-9.25-47.2-35.23-47.2h-20.2c.084 64.107.067 64.113.17 94.32 7.52-.05 16.03-.11 20.8-.2z"/>
        <path d="m404.11 85.9c0-7.99-6.5-14.49-14.5-14.49h-22.23v29.02.04c2.785-.009 14.439-.07 22.23-.07 8 0 14.5-6.5 14.5-14.5z"/>
        <path d="m396.12 130.43c-10.073 0-17.998 0-28.74 0v39.1c10.48-.04 23.49-.09 28.74-.09 10.75 0 19.5-8.75 19.5-19.5 0-10.76-8.75-19.51-19.5-19.51z"/>
      </g>
    </svg>
  ),
  quizBubble: (
    <svg width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
      <g>
        <path d="m473.909 188.982h-121.034c14.132-19.61 22.47-43.66 22.47-69.624 0-65.814-53.544-119.358-119.36-119.358-65.814 0-119.358 53.544-119.358 119.358 0 25.964 8.338 50.014 22.47 69.624h-121.006c-21.003 0-38.091 17.088-38.091 38.091v192.355c0 21.003 17.088 38.091 38.091 38.091h164.723l45.716 51.145c1.897 2.122 4.608 3.335 7.454 3.336h.002c2.846 0 5.557-1.212 7.454-3.333l45.746-51.147h164.724c21.003 0 38.091-17.087 38.091-38.091v-192.356c-.001-21.003-17.089-38.091-38.092-38.091zm-217.924-168.982c54.787 0 99.359 44.572 99.359 99.358s-44.572 99.359-99.359 99.359c-54.786 0-99.358-44.572-99.358-99.359s44.572-99.358 99.358-99.358zm236.015 399.428c0 9.976-8.115 18.091-18.091 18.091h-169.196c-2.846 0-5.557 1.212-7.454 3.333l-41.271 46.145-41.244-46.142c-1.897-2.123-4.609-3.336-7.456-3.336h-169.197c-9.976 0-18.091-8.115-18.091-18.091v-192.355c0-9.975 8.115-18.091 18.091-18.091h139.157c21.03 18.498 48.594 29.735 78.737 29.735s57.708-11.237 78.738-29.735h139.186c9.976 0 18.091 8.116 18.091 18.091z"/>
        <path d="m326.907 266.083c-5.522 0-10 4.477-10 10v96.404c0 5.523 4.478 10 10 10s10-4.477 10-10v-96.404c0-5.523-4.477-10-10-10z"/>
        <path d="m446.641 362.487h-45.866l52.742-80.945c2.003-3.074 2.161-6.998.413-10.224s-5.123-5.235-8.792-5.235h-60.83c-5.522 0-10 4.477-10 10s4.478 10 10 10h42.379l-52.741 80.945c-2.004 3.074-2.162 6.998-.414 10.224s5.123 5.235 8.792 5.235h64.317c5.522 0 10-4.477 10-10s-4.478-10-10-10z"/>
        <path d="m271.265 266.083c-5.522 0-10 4.477-10 10v66.273c0 4.791-1.313 11.822-12.688 17.554-3.376 1.71-7.216 2.577-11.373 2.577-4.683-.018-8.745-1.171-12.439-3.537-7.444-4.741-10.48-9.532-10.48-16.538v-66.33c0-5.523-4.478-10-10-10s-10 4.477-10 10v66.33c0 14.021 6.456 24.949 19.716 33.394 6.871 4.4 14.652 6.647 23.165 6.681 7.365 0 14.245-1.593 20.431-4.726 19.563-9.857 23.67-24.694 23.67-35.405v-66.273c-.002-5.523-4.48-10-10.002-10z"/>
        <path d="m161.439 357.283c6.485-9.389 10.296-20.761 10.296-33.012 0-32.085-26.103-58.188-58.188-58.188-32.101 0-58.217 26.103-58.217 58.188 0 32.101 26.116 58.216 58.217 58.216 12.633 0 24.33-4.062 33.88-10.931l7.606 7.606c1.953 1.953 4.512 2.929 7.071 2.929s5.118-.977 7.071-2.929c3.905-3.905 3.905-10.237 0-14.142zm-47.891 5.204c-21.073 0-38.217-17.144-38.217-38.216 0-21.057 17.144-38.188 38.217-38.188 21.057 0 38.188 17.131 38.188 38.188 0 6.714-1.745 13.025-4.795 18.513l-8.605-8.605c-3.906-3.905-10.236-3.905-14.143 0-3.905 3.905-3.905 10.237 0 14.142l8.814 8.814c-5.703 3.395-12.356 5.352-19.459 5.352z"/>
        <path d="m255.985 175.773c-5.522 0-10 4.477-10 10v.057c0 5.523 4.478 9.972 10 9.972s10-4.505 10-10.028-4.477-10.001-10-10.001z"/>
        <path d="m225.655 93.217c5.522 0 10-4.477 10-10 0-1.043.061-1.972.188-2.861 1.429-10.182 10.271-17.675 20.551-17.5 10.613.229 19.357 8.691 19.907 19.265.293 5.641-1.682 10.997-5.56 15.082-3.885 4.092-9.125 6.345-14.756 6.345-5.522 0-10 4.477-10 10v31.719c0 5.523 4.478 10 10 10s10-4.477 10-10v-22.99c7.29-1.877 14.012-5.774 19.261-11.303 7.691-8.102 11.608-18.717 11.027-29.891-1.091-20.979-18.419-37.768-39.464-38.222-.273-.005-.545-.008-.816-.008-20.058 0-37.158 14.794-39.953 34.7-.259 1.812-.385 3.666-.385 5.665 0 5.522 4.478 9.999 10 9.999z"/>
      </g>
    </svg>
  ),
  leadInBulb: (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="currentColor">
      <g>
        <path d="m25 7.03c0-.552-.447-1-1-1-5.514 0-10 4.486-10 10 0 .552.447 1 1 1s1-.448 1-1c0-4.411 3.589-8 8-8 .553 0 1-.448 1-1z"/>
        <path d="m22.246 45.79h1.754 1.754c1.652 0 2.999-1.345 3-3v-1.099c1.032-.475 1.755-1.512 1.755-2.721v-3.72c.176-.382.281-.802.281-1.25 0-1.016.226-2 .671-2.927.441-.919 1.086-1.752 1.864-2.409 3.746-3.165 5.709-7.989 5.25-12.909-.7-7.375-6.813-13.189-14.265-13.525l-.31-.01-.354.011c-7.408.335-13.521 6.149-14.222 13.526-.458 4.917 1.505 9.742 5.251 12.906.778.658 1.423 1.491 1.864 2.41.445.927.671 1.911.671 2.927 0 .447.105.868.281 1.25v3.721c0 1.209.722 2.247 1.755 2.721v1.1c.001 1.653 1.348 2.998 3 2.998zm4.508-3c0 .552-.449 1-1 1h-1.754-1.754c-.551 0-1-.449-1-1v-.82h2.754 2.754zm1.755-3.819c0 .551-.448 1-1 1h-3.509-3.509c-.552 0-1-.449-1-1v-2.068c.232.058.47.097.719.097h3.79 3.79c.249 0 .487-.039.719-.097zm-9.299-4.971c0-1.318-.292-2.595-.868-3.793-.563-1.171-1.385-2.233-2.376-3.071-3.247-2.742-4.948-6.926-4.551-11.191.607-6.389 5.903-11.426 12.275-11.715l.31-.01.265.009c6.417.29 11.713 5.327 12.319 11.714.398 4.267-1.303 8.451-4.55 11.193-.991.838-1.813 1.9-2.376 3.071-.576 1.198-.868 2.475-.868 3.793 0 .551-.448 1-1 1h-3.79-3.79c-.552 0-1-.449-1-1z"/>
      </g>
    </svg>
  ),
  errorCorrection: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path clipRule="evenodd" d="m19 3h-14c-1.65686 0-3 1.34315-3 3v12c0 1.6569 1.34315 3 3 3h14c1.6569 0 3-1.3431 3-3v-12c0-1.65685-1.3431-3-3-3zm-15 3c0-.55228.44772-1 1-1h14c.5523 0 1 .44772 1 1v12c0 .5523-.4477 1-1 1h-14c-.55228 0-1-.4477-1-1zm1.38906 8.143h1.816v.388c0 .3975.31355.711.711.711.39389 0 .718-.31.718-.711v-.388h.059c.35125 0 .641-.2744.641-.634 0-.3555-.28554-.641-.641-.641h-.059v-2.334c0-.401-.32411-.711-.718-.711-.18711 0-.3409.0382-.48004.12074-.13304.07896-.24021.19166-.34412.31346l-2.12739 2.4703-.0007.0008c-.17777.2087-.29975.4283-.29975.7177 0 .4115.34218.697.725.697zm1.816-1.275h-.66845l.66845-.7835zm4.78814 2.416c1.451 0 2.384-1.2369 2.384-2.734v-.014c0-1.4977-.9335-2.72-2.37-2.72-1.4435 0-2.38397 1.2365-2.38397 2.734v.014c0 1.4968.91877 2.72 2.36997 2.72zm.014-1.373c-.2481 0-.4599-.1235-.6174-.3565-.1604-.2372-.2606-.5856-.2606-1.0045v-.014c0-.4195.0987-.7642.2563-.9976.1544-.2288.3625-.3494.6077-.3494.2435 0 .4557.1241.6149.3593.1616.2386.2631.5872.2631 1.0017v.014c0 .4164-.0971.7613-.254.9956-.1537.2294-.3615.3514-.61.3514zm3.3213.232h1.816v.388c0 .3975.3136.711.711.711.3939 0 .718-.31.718-.711v-.388h.059c.3513 0 .641-.2744.641-.634 0-.3555-.2855-.641-.641-.641h-.059v-2.334c0-.401-.3241-.711-.718-.711-.1871 0-.3409.0382-.48.12074-.1331.07896-.2402.19166-.3442.31346l-2.1273 2.4703-.0007.0008c-.1778.2087-.2998.4283-.2998.7177 0 .4115.3422.697.725.697zm1.816-1.275h-.6684l.6684-.7835zm-12.1445-5.868c0-.55228.44772-1 1-1s1 .44772 1 1-.44772 1-1 1-1-.44772-1-1zm4-1c-.55228 0-1 .44772-1 1s.44772 1 1 1 1-.44772 1-1-.44772-1-1-1z" fillRule="evenodd"/>
    </svg>
  ),
  fourOpinions: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  communicativeSituations: (
    <svg viewBox="0 0 48 48" width="22" height="22" xmlns="http://www.w3.org/2000/svg"><g id="Chat"><path d="m45 1h-28a2 2 0 0 0 -2 2v20a2 2 0 0 0 2 2h18s0 0 8 4v-4h2a2 2 0 0 0 2-2v-20a2 2 0 0 0 -2-2zm-23 14a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm9 0a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm9 0a2 2 0 1 1 2-2 2 2 0 0 1 -2 2z"/><path d="m13 23v-4h-10a2 2 0 0 0 -2 2v20a2 2 0 0 0 2 2h2v4c8.08-4 7.85-4 8-4h18.05a2 2 0 0 0 2-2v-14h-16.05a4 4 0 0 1 -4-4zm-5 10a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm18-4a2 2 0 1 1 -2 2 2 2 0 0 1 2-2zm-7 2a2 2 0 1 1 -2-2 2 2 0 0 1 2 2z"/></g></svg>
  ),
  translation: (
    <svg id="Capa_1" enableBackground="new 0 0 512 512" height="22" viewBox="0 0 512 512" width="22" xmlns="http://www.w3.org/2000/svg">
      <g>
        <g><path fill="currentColor" d="m385.421 379.639-20.889-41.778-20.889 41.778z"/></g>
        <g><path fill="currentColor" d="m467.019 217.065h-21.252v-44.963c0-4.242-1.796-8.285-4.943-11.129s-7.354-4.221-11.571-3.794c-2.388.242-55.886 6.292-82.521 59.886h-21.796v32.889c0 41.345-33.637 74.981-74.981 74.981h-32.889v142.083c0 24.803 20.179 44.981 44.981 44.981h204.972c24.802.001 44.981-20.178 44.981-44.98v-204.972c0-24.803-20.179-44.982-44.981-44.982zm-176.116 200.972 60.212-120.425c2.541-5.082 7.735-8.292 13.417-8.292s10.875 3.21 13.417 8.292l60.213 120.425c3.705 7.41.701 16.419-6.708 20.125s-16.42.702-20.125-6.708l-10.907-21.814h-71.778l-10.907 21.814c-2.628 5.256-7.926 8.295-13.428 8.295-2.254 0-4.543-.51-6.696-1.587-7.411-3.705-10.414-12.715-6.71-20.125z"/></g>
        <g><path fill="currentColor" d="m161.681 126.34h-28.427c1.856 14.861 6.613 27.356 14.214 37.424 7.6-10.068 12.357-22.563 14.213-37.424z"/></g>
        <g><path fill="currentColor" d="m249.954 294.935c24.803 0 44.981-20.179 44.981-44.981v-204.972c0-24.803-20.179-44.982-44.981-44.982h-204.972c-24.803 0-44.982 20.179-44.982 44.982v204.972c0 24.803 20.179 44.981 44.981 44.981h21.252v44.963c0 4.242 1.796 8.285 4.943 11.129 2.77 2.503 6.358 3.871 10.056 3.871.503 0 1.01-.025 1.515-.077 2.388-.242 55.886-6.292 82.521-59.886zm-94.492-86.062c-2.624-1.4-5.304-2.981-7.994-4.74-2.691 1.759-5.37 3.341-7.994 4.74-25.594 13.65-51.143 13.807-52.218 13.807-8.284 0-15-6.716-15-15s6.716-15 15-15c.132-.001 18.639-.27 37.054-9.732-10.536-13.492-18.84-31.799-21.239-56.608h-15.816c-8.284 0-15-6.716-15-15s6.716-15 15-15h45.213v-9.085c0-8.284 6.716-15 15-15s15 6.716 15 15v9.085h45.212c8.284 0 15 6.716 15 15s-6.716 15-15 15h-15.814c-2.4 24.809-10.703 43.116-21.239 56.608 18.414 9.462 36.924 9.73 37.115 9.732 8.284 0 14.969 6.716 14.969 15s-6.747 15-15.031 15c-1.075 0-26.624-.156-52.218-13.807z"/></g>
        <g><path fill="currentColor" d="m365.669 85.819c2.929 2.929 6.768 4.394 10.606 4.394s7.678-1.464 10.606-4.394c5.858-5.858 5.858-15.355 0-21.213l-4.306-4.306c55.209 3.271 99.125 49.215 99.125 105.231 0 8.284 6.716 15 15 15s15-6.716 15-15c0-72.673-57.543-132.151-129.458-135.286l4.639-4.639c5.858-5.858 5.858-15.355 0-21.213-5.857-5.857-15.355-5.858-21.213 0l-30.105 30.107c-5.858 5.858-5.858 15.355 0 21.213z"/></g>
        <g><path fill="currentColor" d="m146.032 425.881c-5.857-5.858-15.355-5.858-21.213 0s-5.858 15.355 0 21.213l4.306 4.306c-55.209-3.27-99.125-49.215-99.125-105.231 0-8.284-6.716-15-15-15s-15 6.716-15 15c0 72.673 57.543 132.151 129.458 135.286l-4.639 4.639c-5.858 5.858-5.858 15.355 0 21.213 2.929 2.929 6.768 4.393 10.606 4.393s7.678-1.464 10.606-4.394l30.106-30.106c5.858-5.858 5.858-15.355 0-21.213z"/></g>
      </g>
    </svg>
  ),
  matchingHalves: (
    <svg height="20" viewBox="0 0 512 512" width="20" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
      <path d="m0 271c0 126.019531 99.394531 232.195312 225.019531 240.472656l15.980469.527344v-481.527344l-15.980469 1.054688c-125.625 8.277344-225.019531 113.453125-225.019531 239.472656zm0 0"/>
      <path d="m286.980469 1.054688-15.980469-1.054688v481.527344l15.980469-1.054688c125.625-8.277344 225.019531-113.453125 225.019531-239.472656s-99.394531-231.667969-225.019531-239.945312zm0 0"/>
    </svg>
  ),
};

// SVG-иконки
const searchIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{marginRight:8,verticalAlign:'middle'}}><circle cx="9" cy="9" r="7" stroke="#8a2be2" strokeWidth="2"/><path d="M15 15L18 18" stroke="#8a2be2" strokeWidth="2" strokeLinecap="round"/></svg>
);

const magicIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#8a2be2" viewBox="0 0 16 16" style={{marginRight:8,verticalAlign:'middle'}}>
    <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707zM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1zM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707zM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0z"/>
  </svg>
);

const smartLessonStarsIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="#8a2be2"
    viewBox="0 0 16 16"
    style={{ marginRight: 8, verticalAlign: "middle" }}
  >
    <path d="M7.657 6.247c.11-.33.576-.33.686 0l.645 1.937a2.89 2.89 0 0 0 1.829 1.828l1.936.645c.33.11.33.576 0 .686l-1.937.645a2.89 2.89 0 0 0-1.828 1.829l-.645 1.936a.361.361 0 0 1-.686 0l-.645-1.937a2.89 2.89 0 0 0-1.828-1.828l-1.937-.645a.361.361 0 0 1 0-.686l1.937-.645a2.89 2.89 0 0 0 1.828-1.828zM3.794 1.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387A1.73 1.73 0 0 0 4.593 5.69l-.387 1.162a.217.217 0 0 1-.412 0L3.407 5.69A1.73 1.73 0 0 0 2.31 4.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387A1.73 1.73 0 0 0 3.407 2.31zM10.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.16 1.16 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.16 1.16 0 0 0-.732-.732L9.1 2.137a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732z" />
  </svg>
);

const audioIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13"/>
    <circle cx="6" cy="18" r="3"/>
    <circle cx="18" cy="16" r="3"/>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const UNSPLASH_ACCESS_KEY = '2vQn6QwQn6QwQn6QwQn6QwQn6QwQn6QwQn6QwQn6QwQn6QwQn6QwQn6QwQn6QwQn6Qw'; // demo key, replace with your own

const MENU_OPTIONS = [
  { label: "Add Materials", icon: icons.file, action: (activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal) => {
      setMaterialsSelectorModal(true);
    }
  },
  { label: "H1 Heading", icon: icons.h1, action: activeEditor => activeEditor.chain().focus().toggleHeading({ level: 1 }).run() },
  { label: "H2 Subheading", icon: icons.h2, action: activeEditor => activeEditor.chain().focus().toggleHeading({ level: 2 }).run() },
  { label: "Add Table", icon: icons.table, action: (activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal) => {} },
  { label: "Divider", icon: icons.divider, action: activeEditor => activeEditor.chain().focus().setHorizontalRule().run() },
  { label: "Checklist", icon: icons.checklist, action: activeEditor => activeEditor.chain().focus().toggleTaskList().run() },
  { label: "Bulleted List", icon: icons.bullet, action: activeEditor => activeEditor.chain().focus().toggleBulletList().run() },
  { label: "Numbered List", icon: icons.number, action: activeEditor => activeEditor.chain().focus().toggleOrderedList().run() },
  { label: "Image", icon: icons.image,
    action: (activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = e => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = evt => {
          activeEditor.chain().focus().setImage({
            src: evt.target.result,
            width: '400px',
            height: 'auto',
            align: 'center',
            draggable: true,
            class: 'resizable-image',
            imageId: `img_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`
          }).run();
        };
        reader.readAsDataURL(file);
      };
      input.click();
    },
    boardAction: (onInsertToBoard) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = e => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = evt => {
          const ext = file.name.split('.').pop().toLowerCase();
          onInsertToBoard({ type: 'file', value: evt.target.result, name: file.name, extension: ext }, 'material');
        };
        reader.readAsDataURL(file);
      };
      input.click();
    },
  },
  { label: "YouTube Video", icon: icons.youtube,
    action: (activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal) => {
      const url = window.prompt('Enter YouTube URL:');
      if (!url) return;
      const videoId = extractYouTubeId(url);
      if (!videoId) { alert('Invalid YouTube URL'); return; }
      activeEditor.chain().focus().insertContent({
        type: 'youtube',
        attrs: { src: `https://www.youtube.com/embed/${videoId}`, width: '560', height: '315' }
      }).run();
    },
    boardAction: (onInsertToBoard) => {
      const url = window.prompt('Enter YouTube URL:');
      if (!url) return;
      const videoId = extractYouTubeId(url);
      if (!videoId) { alert('Invalid YouTube URL'); return; }
      onInsertToBoard({ type: 'link', value: `https://www.youtube.com/watch?v=${videoId}`, name: 'YouTube Video' }, 'material');
    },
  },
  { label: "Link", icon: icons.link,
    action: activeEditor => {
      const url = window.prompt("Link URL:");
      if (url) activeEditor.chain().focus().setLink({ href: url }).run();
    },
    boardAction: (onInsertToBoard) => {
      const url = window.prompt("Link URL:");
      if (!url) return;
      onInsertToBoard({ type: 'link', value: url, name: url }, 'material');
    },
  },
  { label: "PDF/DOCX", icon: icons.file,
    action: (activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';
      input.onchange = e => {
        const file = e.target.files[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        const filename = file.name;
        if (file.type === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) {
          setPdfModal({ url, filename, activeEditor });
        } else if (file.type.includes('word') || filename.toLowerCase().endsWith('.docx') || filename.toLowerCase().endsWith('.doc')) {
          activeEditor.chain().focus().insertFileEmbed({ url, filename, type: 'docx' }).run();
        } else {
          alert('Only PDF and DOCX files are supported.');
        }
      };
      input.click();
    },
    boardAction: (onInsertToBoard) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';
      input.onchange = e => {
        const file = e.target.files[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        const ext = file.name.split('.').pop().toLowerCase();
        onInsertToBoard({ type: 'file', value: url, name: file.name, extension: ext }, 'material');
      };
      input.click();
    },
  },
  { label: "Search Image Online", icon: icons.image,
    action: (activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal) => { setShowUnsplash(true); },
    boardAction: (onInsertToBoard, setShowUnsplash) => { setShowUnsplash(true); },
  },
  { label: "Audio", icon: audioIcon,
    action: (activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'audio/*';
      input.onchange = e => {
        const file = e.target.files[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        activeEditor.chain().focus().insertContent({
          type: 'audioNode',
          attrs: { src: url, controls: true, width: '400px', height: 'auto' }
        }).run();
      };
      input.click();
    },
    boardAction: (onInsertToBoard) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'audio/*';
      input.onchange = e => {
        const file = e.target.files[0];
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        const url = URL.createObjectURL(file);
        onInsertToBoard({ type: 'file', value: url, name: file.name, extension: ext }, 'material');
      };
      input.click();
    },
  },
];

const TEMPLATES = [
  { label: "Create ABCD Questions for a Text", prompt: "Create a list of ABCD questions for the following text. Only one correct answer per question.", icon: icons.abcd, tag: '#reading' },
  { label: "Create Open Questions for a Text", prompt: "Create a list of open questions for the following text.", icon: icons.questionBubble, tag: '#reading' },
  { label: "Word-Definition Matching", prompt: "Create a matching exercise for these words: match each word to its meaning.", icon: icons.wordDefinition, tag: '#vocabulary' },
  { label: "Fill in the Gaps Exercise", type: "fillInTheGaps", icon: icons.columnsGap, tags: ['#vocabulary', '#grammar'] },
  { label: "Create True/False Statements for a Text", prompt: "Create a true/false reading exercise for the following text.", icon: icons.trueFalse, tag: '#reading' },
  { label: "Warm-Up Discussion Questions", prompt: "Create warm-up discussion questions for the following topic or text.", type: "warmUpDiscussion", icon: icons.fire, tag: '#speaking' },
  { label: "Create a Dialogue on Any Topic", prompt: "Create a dialogue based on the following text or topic.", type: "createDialogue", icon: icons.chatBubble, tags: ['#reading', '#speaking'] },
  { label: "Essential Vocabulary on a Topic", prompt: "Suggest a list of essential vocabulary items for the following topic.", icon: icons.bookmarkStar, tag: '#vocabulary' },
  { label: "Lead-in Activities for a Text", prompt: "Create 3 lead-in activity ideas for the following text.", icon: icons.leadInBulb, tag: '#speaking' },
  { label: "Four Opinions", type: "fourOpinions", icon: icons.fourOpinions, tag: '#speaking' },
  { label: "Create Communicative Situations with Your Vocabulary", type: "communicativeSituations", tag: '#vocabulary', icon: (
    <svg viewBox="0 0 48 48" width="22" height="22" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><g id="Chat"><path d="m45 1h-28a2 2 0 0 0 -2 2v20a2 2 0 0 0 2 2h18s0 0 8 4v-4h2a2 2 0 0 0 2-2v-20a2 2 0 0 0 -2-2zm-23 14a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm9 0a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm9 0a2 2 0 1 1 2-2 2 2 0 0 1 -2 2z"/><path d="m13 23v-4h-10a2 2 0 0 0 -2 2v20a2 2 0 0 0 2 2h2v4c8.08-4 7.85-4 8-4h18.05a2 2 0 0 0 2-2v-14h-16.05a4 4 0 0 1 -4-4zm-5 10a2 2 0 1 1 2-2 2 2 0 0 1 -2 2zm18-4a2 2 0 1 1 -2 2 2 2 0 0 1 2-2zm-7 2a2 2 0 1 1 -2-2 2 2 0 0 1 2 2z"/></g></svg>
  ) },
  { label: "Word-Translation Matching", prompt: "Translate these words/phrases into different languages and create a matching exercise.", icon: (<span style={{color:'currentColor'}}>{icons.translation}</span>), tag: '#vocabulary' },
  { label: "Create an Exercise with Matching Halves", prompt: "Create an exercise where students match halves of collocations, sentences, etc.", icon: icons.matchingHalves, tag: '#vocabulary' },
  { label: "Error Correction Exercise", prompt: "Create comprehensive error correction exercises with realistic grammar mistakes that students commonly make. Include various error types: verb tenses, articles, prepositions, word order, subject-verb agreement, and more. Make mistakes natural and challenging but appropriate for the CEFR level.", icon: icons.errorCorrection, tag: '#grammar' },
  { label: "Grammar Rules", prompt: "Produce customized grammar rules and explanations for the following topic.", icon: icons.grammarRules, tag: '#grammar' },
  { label: "Multiple Choice", prompt: "Create a multiple choice exercise.", icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/>
    </svg>
  ), tag: '#grammar' },
  { label: "Word Order / Jumbled Sentences", prompt: "Create a word order exercise.", icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
      <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
      <line x1="4" y1="4" x2="9" y2="9"/>
    </svg>
  ), tag: '#grammar' },
  { label: "Generate Interactive Quiz", type: "quiz", icon: icons.quizBubble, tag: '#game' },
  { label: "Create a List of Pros and Cons", prompt: "Create a list of pros and cons on the following topic. Return two lists: one for pros, one for cons. Each item should be a clear, simple sentence. The language and complexity must match CEFR level.", icon: icons.balance, type: "prosCons", tag: '#speaking' },
];

export default function CommandMenu({ editor, cursorPosition = { x: 0, y: 0 }, onInsertToBoard, onInsertSmartLesson, currentTab = 0 }) {

  const { activeEditor } = useContext(ActiveEditorContext);
  const {
    plusPos, dragging,
    show, setShow, coords, setCoords, search, setSearch, selected, setSelected,
    showTablePicker, setShowTablePicker, scrollY, setMenuSource,
    plusRef, menuRef, inputRef, onMouseDown,
  } = useMenuVisibility({ editor });
  const {
    showAI, setShowAI, showAIPopup, setShowAIPopup, showAskMe, setShowAskMe,
    showSmartLesson, setShowSmartLesson, setAskMeInput,
    askMeLoading, setAskMeLoading, setAskMeResult,
    askMeMessages, setAskMeMessages, askMeCurrentMessage, setAskMeCurrentMessage,
    askMeMessagesEndRef, popupRef,
  } = useAskMeState();

  // Функции для диалога Ask Me
  const handleAskMeSendMessage = async () => {
    if (!askMeCurrentMessage.trim()) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: askMeCurrentMessage,
      timestamp: new Date()
    };
    
    setAskMeMessages(prev => [...prev, userMessage]);
    const messageToSend = askMeCurrentMessage;
    setAskMeCurrentMessage("");
    setAskMeLoading(true);
    
    try {
      const content = await fetchOpenAIText({
        prompt: messageToSend,
        temperature: 0.7,
        maxTokens: 800,
      });
      
      if (content) {
        const assistantMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: content.trim(),
          timestamp: new Date()
        };
        setAskMeMessages(prev => [...prev, assistantMessage]);
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: 'Sorry, an error occurred. Please try again.',
          timestamp: new Date()
        };
        setAskMeMessages(prev => [...prev, errorMessage]);
      }
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: 'Sorry, an error occurred. Please try again.',
        timestamp: new Date()
      };
      setAskMeMessages(prev => [...prev, errorMessage]);
    } finally {
      setAskMeLoading(false);
    }
  };

  const clearAskMeHistory = () => {
    setAskMeMessages([
      { 
        id: 1, 
        type: 'assistant', 
        content: 'Hello! I am an AI assistant for creating exercises. How can I help you?',
        timestamp: new Date()
      }
    ]);
  };

  const {
    showTemplates, setShowTemplates, templateTagFilter, setTemplateTagFilter,
    selectedTemplate, setSelectedTemplate, exerciseInput, setExerciseInput,
    setExerciseLoading, exerciseResult, setExerciseResult,
    setShowExerciseInput, setShowExerciseResult,
    pdfModal, setPdfModal, materialsSelectorModal, setMaterialsSelectorModal,
  } = useTemplateState();

  // Helper functions for YouTube links
  const isYouTubeLink = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return youtubeRegex.test(url);
  };
  
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Function to handle material selection for editor
  const handleMaterialSelect = (material) => {
    if (!activeEditor) {
      return;
    }
    
    if (material.type === 'file') {
      const extension = material.extension || material.name.split('.').pop().toLowerCase();
      
      if (extension === 'pdf') {
        // Открываем модальное окно выбора страниц PDF
        setPdfModal({ 
          url: material.value, 
          filename: material.name, 
          activeEditor 
        });
      } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
        // Вставляем изображение
        activeEditor.chain().focus().setImage({
          src: material.value,
          width: '400px',
          height: 'auto',
          align: 'center',
          draggable: true,
          class: 'resizable-image',
          imageId: `img_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`
        }).run();
      } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        // Вставляем видео
        activeEditor.chain().focus().insertContent({
          type: 'video',
          attrs: {
            src: material.value,
            controls: true,
            width: '400px',
            height: 'auto'
          }
        }).run();
      } else if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
        // Вставляем аудио
        activeEditor.chain().focus().insertContent({
          type: 'audioNode',
          attrs: {
            src: material.value,
            controls: true,
            width: '400px',
            height: 'auto'
          }
        }).run();
      } else {
        // Вставляем файл напрямую
        activeEditor.chain().focus().insertContent({
          type: 'file',
          attrs: {
            src: material.value,
            filename: material.name,
            size: material.file?.size || 0,
            type: material.file?.type || 'application/octet-stream'
          }
        }).run();
      }
    } else if (material.type === 'link') {
      // Проверяем, является ли ссылка YouTube видео
      if (isYouTubeLink(material.value)) {
        const videoId = getYouTubeId(material.value);
        if (videoId) {
          // Вставляем YouTube видео
          activeEditor.chain().focus().insertContent({
            type: 'youtube',
            attrs: {
              src: `https://www.youtube.com/embed/${videoId}`,
              width: '400px',
              height: '300px'
            }
          }).run();
        } else {
          // Если не удалось извлечь ID, вставляем как обычную ссылку
          activeEditor.chain().focus().setLink({ href: material.value }).run();
        }
      } else {
        // Вставляем обычную ссылку
        activeEditor.chain().focus().setLink({ href: material.value }).run();
      }
    }
    
    setMaterialsSelectorModal(false);
  };
  
  const {
    showQuizGenerator, setShowQuizGenerator, quizInput, setQuizInput, quizLoading, setQuizLoading,
    quizLevel, setQuizLevel, quizCount, setQuizCount, generatedQuiz, setGeneratedQuiz, quizType, setQuizType,
    showABCDGenerator, setShowABCDGenerator, abcdInput, setABCDInput, abcdLevel, setABCDLevel,
    abcdLoading, setABCDLoading, generatedABCD, setGeneratedABCD, abcdQuestionCount, setABCDQuestionCount,
    showOpenQuestionsGenerator, setShowOpenQuestionsGenerator, openQuestionsInput, setOpenQuestionsInput,
    openQuestionsLevel, setOpenQuestionsLevel, openQuestionsLoading, setOpenQuestionsLoading,
    generatedOpenQuestions, setGeneratedOpenQuestions, openQuestionsCount, setOpenQuestionsCount,
    showWordDefinitionGenerator, setShowWordDefinitionGenerator, wordDefinitionInput, setWordDefinitionInput,
    wordDefinitionLevel, setWordDefinitionLevel, wordDefinitionLoading, setWordDefinitionLoading,
    generatedWordDefinitions, setGeneratedWordDefinitions,
    showWordTranslationGenerator, setShowWordTranslationGenerator, wordTranslationInput, setWordTranslationInput,
    wordTranslationTargetLanguage, setWordTranslationTargetLanguage, wordTranslationLoading, setWordTranslationLoading,
    generatedWordTranslations, setGeneratedWordTranslations,
    showFillInTheGapsGenerator, setShowFillInTheGapsGenerator, fillInTheGapsInput, setFillInTheGapsInput,
    fillInTheGapsLevel, setFillInTheGapsLevel, fillInTheGapsLoading, setFillInTheGapsLoading,
    generatedFillInTheGaps, setGeneratedFillInTheGaps,
    fillInTheGapsMode, setFillInTheGapsMode, fillInTheGapsCustomText, setFillInTheGapsCustomText,
    showTrueFalseGenerator, setShowTrueFalseGenerator, trueFalseInput, setTrueFalseInput,
    trueFalseLevel, setTrueFalseLevel, trueFalseCount, setTrueFalseCount, trueFalseLoading, setTrueFalseLoading,
    generatedTrueFalse, setGeneratedTrueFalse,
    showWarmUpDiscussionGenerator, setShowWarmUpDiscussionGenerator, warmUpDiscussionInput, setWarmUpDiscussionInput,
    warmUpDiscussionLevel, setWarmUpDiscussionLevel, warmUpDiscussionCount, setWarmUpDiscussionCount,
    warmUpDiscussionLoading, setWarmUpDiscussionLoading, generatedWarmUpDiscussion, setGeneratedWarmUpDiscussion,
    showCreateDialogueGenerator, setShowCreateDialogueGenerator, createDialogueInput, setCreateDialogueInput,
    createDialogueLevel, setCreateDialogueLevel, createDialogueSpeakers, setCreateDialogueSpeakers,
    createDialogueLoading, setCreateDialogueLoading, generatedCreateDialogue, setGeneratedCreateDialogue,
    showEssentialVocabularyGenerator, setShowEssentialVocabularyGenerator,
    essentialVocabularyMode, setEssentialVocabularyMode, essentialVocabularyInput, setEssentialVocabularyInput,
    essentialVocabularyLevel, setEssentialVocabularyLevel, essentialVocabularyPartOfSpeech, setEssentialVocabularyPartOfSpeech,
    essentialVocabularyTranslationLanguage, setEssentialVocabularyTranslationLanguage,
    essentialVocabularyLoading, setEssentialVocabularyLoading, generatedEssentialVocabulary, setGeneratedEssentialVocabulary,
    showLeadInActivitiesGenerator, setShowLeadInActivitiesGenerator, leadInActivitiesInput, setLeadInActivitiesInput,
    leadInActivitiesLevel, setLeadInActivitiesLevel, leadInActivitiesLoading, setLeadInActivitiesLoading,
    generatedLeadInActivities, setGeneratedLeadInActivities,
    showFourOpinionsGenerator, setShowFourOpinionsGenerator, fourOpinionsInput, setFourOpinionsInput,
    fourOpinionsLevel, setFourOpinionsLevel, fourOpinionsCount, setFourOpinionsCount,
    fourOpinionsLoading, setFourOpinionsLoading, generatedFourOpinions, setGeneratedFourOpinions,
    showCommunicativeSituationsGenerator, setShowCommunicativeSituationsGenerator,
    communicativeSituationsVocab, setCommunicativeSituationsVocab,
    communicativeSituationsTopic, setCommunicativeSituationsTopic,
    communicativeSituationsLevel, setCommunicativeSituationsLevel,
    communicativeSituationsLoading, setCommunicativeSituationsLoading,
    generatedCommunicativeSituations, setGeneratedCommunicativeSituations,
    showMatchingHalvesGenerator, setShowMatchingHalvesGenerator, matchingHalvesInput, setMatchingHalvesInput,
    matchingHalvesLevel, setMatchingHalvesLevel, matchingHalvesMode, setMatchingHalvesMode,
    matchingHalvesLoading, setMatchingHalvesLoading, generatedMatchingHalves, setGeneratedMatchingHalves,
    showErrorCorrectionGenerator, setShowErrorCorrectionGenerator, errorCorrectionInput, setErrorCorrectionInput,
    errorCorrectionLevel, setErrorCorrectionLevel, errorCorrectionSentenceCount, setErrorCorrectionSentenceCount,
    errorCorrectionLoading, setErrorCorrectionLoading, generatedErrorCorrection, setGeneratedErrorCorrection,
    showGrammarRulesGenerator, setShowGrammarRulesGenerator, grammarRulesInput, setGrammarRulesInput,
    grammarRulesLevel, setGrammarRulesLevel, grammarRulesLanguage, setGrammarRulesLanguage,
    grammarRulesLoading, setGrammarRulesLoading, generatedGrammarRules, setGeneratedGrammarRules,
    showProsConsGenerator, setShowProsConsGenerator, prosConsInput, setProsConsInput,
    prosConsLevel, setProsConsLevel, prosConsPairs, setProsConsPairs,
    prosConsLoading, setProsConsLoading, generatedProsCons, setGeneratedProsCons,
    showJumbledSentenceGenerator, setShowJumbledSentenceGenerator,
    jumbledSentenceInput, setJumbledSentenceInput,
    jumbledSentenceLevel, setJumbledSentenceLevel,
    jumbledSentenceCount, setJumbledSentenceCount,
    jumbledSentenceLoading, setJumbledSentenceLoading,
    generatedJumbledSentence, setGeneratedJumbledSentence,
    jumbledSentenceMode, setJumbledSentenceMode,
    showMultipleChoiceGenerator, setShowMultipleChoiceGenerator,
    multipleChoiceInput, setMultipleChoiceInput,
    multipleChoiceLevel, setMultipleChoiceLevel,
    multipleChoiceMode, setMultipleChoiceMode,
    multipleChoiceCount, setMultipleChoiceCount,
    multipleChoiceOptionCount, setMultipleChoiceOptionCount,
    multipleChoiceLoading, setMultipleChoiceLoading,
    generatedMultipleChoice, setGeneratedMultipleChoice,
  } = useExerciseGenerators();

  const {
    showUnsplash, setShowUnsplash, unsplashQuery, setUnsplashQuery,
    unsplashResults, setUnsplashResults, unsplashLoading, setUnsplashLoading,
  } = useUnsplashState();

  // Фильтрация опций с мемоизацией
  const filteredOptions = useMemo(() => {
    const hiddenLabels = [
      "H1 Heading",
      "H2 Subheading",
      "Bulleted List",
      "Numbered List",
      "Link"
    ];
    return MENU_OPTIONS.filter(opt =>
      !hiddenLabels.includes(opt.label) &&
      opt.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  // Drag and Drop functionality
  useEffect(() => {
    if (!editor) return;
    
    const editorElement = editor.view.dom;
    let dragDepth = 0;
    
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth += 1;
      editorElement.classList.add('is-dragover');
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      editorElement.classList.add('is-dragover');
    };
    
    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) {
        editorElement.classList.remove('is-dragover');
      }
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth = 0;
      editorElement.classList.remove('is-dragover');
      
      const files = Array.from(e.dataTransfer.files);

      files.forEach(file => {
        const filename = file.name;

        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = evt => {
            activeEditor.chain().focus().setImage({
              src: evt.target.result,
              width: '400px',
              height: 'auto',
              align: 'center',
              draggable: true,
              class: 'resizable-image',
              imageId: `img_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`
            }).run();
          };
          reader.readAsDataURL(file);
        } else if (file.type.startsWith('video/')) {
          const url = URL.createObjectURL(file);
          activeEditor.chain().focus().insertContent({
            type: 'resizableVideo',
            attrs: { src: url, controls: true, width: '400px', height: 'auto' }
          }).run();
        } else if (file.type.startsWith('audio/')) {
          const url = URL.createObjectURL(file);
          activeEditor.chain().focus().insertContent({
            type: 'audioNode',
            attrs: { src: url, controls: true, width: '400px', height: 'auto' }
          }).run();
        } else if (file.type === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) {
          const url = URL.createObjectURL(file);
          setPdfModal({ url, filename, activeEditor });
        } else if (file.type.includes('word') || filename.toLowerCase().endsWith('.docx') || filename.toLowerCase().endsWith('.doc')) {
          const url = URL.createObjectURL(file);
          activeEditor.chain().focus().insertFileEmbed({ url, filename, type: 'docx' }).run();
        }
      });
    };
    
    editorElement.addEventListener('dragenter', handleDragEnter);
    editorElement.addEventListener('dragover', handleDragOver);
    editorElement.addEventListener('dragleave', handleDragLeave);
    editorElement.addEventListener('drop', handleDrop);
    
    return () => {
      editorElement.removeEventListener('dragenter', handleDragEnter);
      editorElement.removeEventListener('dragover', handleDragOver);
      editorElement.removeEventListener('dragleave', handleDragLeave);
      editorElement.removeEventListener('drop', handleDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, setPdfModal]);

  // Обработка навигации по меню
  useEffect(() => {
    if (!show) return;
    const onKeyDown = e => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected(s => (s + 1) % filteredOptions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected(s => (s - 1 + filteredOptions.length) % filteredOptions.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleOptionClick(filteredOptions[selected], selected);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selected, filteredOptions]);

  const handleOptionClick = useCallback((opt, i) => {
    if (opt.label === "Add Table") {
      setShowTablePicker(true);
      return;
    }
    // On whiteboard: use boardAction if available, else skip editor-only actions
    if (currentTab > 0 && onInsertToBoard) {
      if (opt.boardAction) {
        opt.boardAction(onInsertToBoard, setShowUnsplash, setPdfModal, setMaterialsSelectorModal);
      } else if (activeEditor) {
        // Text element is focused — editor actions work inside it
        opt.action(activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal, setShowTemplates, setShowAI, setShowQuizGenerator);
      }
      // editor-only options (H1, table, etc.) silently no-op when no text element focused
    } else if (activeEditor) {
      opt.action(activeEditor, setShowUnsplash, setPdfModal, setMaterialsSelectorModal, setShowTemplates, setShowAI, setShowQuizGenerator);
    } else {
      opt.action(null, setShowUnsplash, setPdfModal, setMaterialsSelectorModal, setShowTemplates, setShowAI, setShowQuizGenerator);
    }
    setShow(false);
    setSearch("");
    setSelected(0);
  }, [activeEditor, currentTab, onInsertToBoard, setShowUnsplash, setPdfModal, setMaterialsSelectorModal, setShowTemplates, setShowAI, setShowQuizGenerator, setShow, setSearch, setSelected, setShowTablePicker]);

  const handleTemplateSelect = useCallback((template) => {
    setSelectedTemplate(template);
    setShowTemplates(false);
    
    if (template.type === 'quiz') {
      setShowQuizGenerator(true);
    } else if (template.type === 'fillInTheGaps') {
      setShowFillInTheGapsGenerator(true);
    } else if (template.type === 'createDialogue') {
      setShowCreateDialogueGenerator(true);
    } else if (template.type === 'fourOpinions') {
      setShowFourOpinionsGenerator(true);
    } else if (template.type === 'communicativeSituations') {
      setShowCommunicativeSituationsGenerator(true);
    } else if (template.label === "Create ABCD Questions for a Text") {
      setShowABCDGenerator(true);
    } else if (template.label === "Create Open Questions for a Text") {
      setShowOpenQuestionsGenerator(true);
    } else if (template.label === "Word-Definition Matching") {
      setShowWordDefinitionGenerator(true);
    } else if (template.label === "Word-Translation Matching") {
      setShowWordTranslationGenerator(true);
    } else if (template.label === "Create True/False Statements for a Text") {
      setShowTrueFalseGenerator(true);
    } else if (template.label === "Warm-Up Discussion Questions") {
      setShowWarmUpDiscussionGenerator(true);
    } else if (template.label === "Essential Vocabulary on a Topic") {
      setShowEssentialVocabularyGenerator(true);
    } else if (template.label === "Lead-in Activities for a Text") {
      setShowLeadInActivitiesGenerator(true);
    } else if (template.type === 'prosCons') {
      setShowProsConsGenerator(true);
    } else if (template.label === "Create an Exercise with Matching Halves") {
      setShowMatchingHalvesGenerator(true);
    } else if (template.label === "Error Correction Exercise") {
      setShowErrorCorrectionGenerator(true);
    } else if (template.label === "Grammar Rules") {
      setShowGrammarRulesGenerator(true);
    } else if (template.label === "Multiple Choice") {
      setShowMultipleChoiceGenerator(true);
    } else if (template.label === "Word Order / Jumbled Sentences") {
      setShowJumbledSentenceGenerator(true);
    } else {
      setShowExerciseInput(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line no-unused-vars
  const handleExerciseGenerate = async () => {
    if (!exerciseInput.trim()) return;
    
    setExerciseLoading(true);
    try {
      const prompt = `${selectedTemplate.prompt}\n\n${exerciseInput}`;
      const content = await fetchOpenAIText({ prompt, temperature: 0.7, maxTokens: 1500 });
      if (content) {
        setExerciseResult(content.trim());
        setShowExerciseResult(true);
      }
    } catch (e) {
      // Error generating exercise
    } finally {
      setExerciseLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleExerciseInsert = () => {
    if (exerciseResult && activeEditor) {
      activeEditor.chain().focus().insertContent(exerciseResult).run();
    } else if (exerciseResult && !activeEditor) {
      // No active editor available
    }
    setShowExerciseResult(false);
    setShowExerciseInput(false);
    setExerciseInput("");
    setExerciseResult("");
    setSelectedTemplate(null);
  };

  // Поиск по Unsplash
  const searchUnsplash = async (query) => {
    if (!query || !query.trim()) return;
    setUnsplashLoading(true);
    setUnsplashResults([]);
    try {
      const res = await fetch(`/api/images/search?q=${encodeURIComponent(query.trim())}&per_page=12`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const results = await res.json();
      setUnsplashResults(Array.isArray(results) ? results : []);
    } catch (e) {
      console.warn('[searchUnsplash] error:', e?.message);
      setUnsplashResults([]);
    }
    setUnsplashLoading(false);
  };

  const handlePdfModalClose = () => {
    setPdfModal(null);
  };

  const handlePdfPagesInsert = (selectedPagesData) => {
    if (!pdfModal) return;
    
    // Вставляем в текстовый редактор
    const content = [];
    
    selectedPagesData.forEach((page, index) => {
      const imageUrl = page.canvas.toDataURL();
      const filename = `${pdfModal.filename} - Page ${page.pageNumber}`;
      

      
      content.push({
        type: 'image',
        attrs: {
          src: imageUrl,
          alt: filename,
          title: filename,
          width: page.width > page.height ? '800px' : '600px',
          height: 'auto',
          imageId: `img_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`,
          data: {
            cursorX: cursorPosition.x,
            cursorY: cursorPosition.y
          }
        }
      });
      
      if (index < selectedPagesData.length - 1) {
        content.push({
          type: 'paragraph',
          content: []
        });
      }
    });
    

    
    if (activeEditor && activeEditor.chain) {
      activeEditor.chain().focus().insertContent(content).run();
    }
    
    setPdfModal(null);
  };

  // Если activeEditor не передан, все равно рендерим кнопки, но без функциональности редактора

  return (
    <div className="command-menu-float" style={{
      position: "fixed",
      top: Math.max(20, plusPos.top - scrollY),
      left: plusPos.left,
      zIndex: 2147483647,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0
    }}>
      <button
        ref={plusRef}
        data-tour="lesson-add-elements"
        style={{
          width: 36, height: 36, borderRadius: "50%", background: "#fff", boxShadow: "0 2px 12px #8a2be222", border: "1.5px solid #cfcfff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, cursor: dragging ? "grabbing" : "grab", transition: "box-shadow 0.2s, background 0.2s", marginTop: 0, marginBottom: 0, color: '#000'
        }}
        onClick={e => {
  
          setMenuSource('plus');
          setShow(prev => {
            const next = !prev;

            if (next) {
              setCoords({
                top: 80,
                left: 35
              });
              setTimeout(() => {
                if (inputRef.current) inputRef.current.focus();
              }, 0);
            }
            return next;
          });
        }}
        onMouseDown={onMouseDown}
        title="Add elements"
        onMouseEnter={e => e.currentTarget.style.background = '#f3eafd'}
        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
      >
        +
      </button>
      <span data-tour="lesson-add-exercises" style={{ display: 'inline-flex', marginTop: 12 }}>
        <StarCircleButton title="Add exercises" onClick={() => setShowAIPopup(true)} />
      </span>
      {showAIPopup && (
        <div
          ref={popupRef}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
          position: "absolute",
          top: 80,
          left: 35,
          zIndex: 1500,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 4px 24px #8a2be244",
          padding: 12,
          width: 420,
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          border: '1.5px solid #cfcfff',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          alignItems: 'stretch',
          animation: 'fadeInAI 0.28s cubic-bezier(.4,0,.2,1)',
          transition: 'opacity 0.28s cubic-bezier(.4,0,.2,1), transform 0.28s cubic-bezier(.4,0,.2,1)',
        }}
        >
          {!showSmartLesson && !showAskMe && !showTemplates && !selectedTemplate && !exerciseResult && !showQuizGenerator && !showABCDGenerator && !showOpenQuestionsGenerator && !showWordDefinitionGenerator && !showWordTranslationGenerator && !showFillInTheGapsGenerator && !showTrueFalseGenerator && !showWarmUpDiscussionGenerator && !showCreateDialogueGenerator && !showEssentialVocabularyGenerator && !showLeadInActivitiesGenerator && !showFourOpinionsGenerator && !showCommunicativeSituationsGenerator && !showMatchingHalvesGenerator && !showErrorCorrectionGenerator && !showGrammarRulesGenerator && !showJumbledSentenceGenerator && !showMultipleChoiceGenerator ? (
            <>
              <button
                data-tour="lesson-ai-askme"
                style={{ fontSize: 15, fontWeight: 400, color: '#222', background: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', paddingLeft: 18, cursor: 'pointer', width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', transition: 'background 0.22s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#ede2f9'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                onClick={() => { setShowAskMe(true); setShowTemplates(false); setSelectedTemplate(null); setExerciseResult(""); setShowQuizGenerator(false); setShowABCDGenerator(false); setShowOpenQuestionsGenerator(false); setShowWordDefinitionGenerator(false); setShowWordTranslationGenerator(false); setShowFillInTheGapsGenerator(false); setShowTrueFalseGenerator(false); setShowWarmUpDiscussionGenerator(false); setShowCreateDialogueGenerator(false); setShowEssentialVocabularyGenerator(false); setShowLeadInActivitiesGenerator(false); setShowFourOpinionsGenerator(false); setShowCommunicativeSituationsGenerator(false); setShowMatchingHalvesGenerator(false); setShowErrorCorrectionGenerator(false); setShowGrammarRulesGenerator(false); }}
              >
                {searchIcon} Ask Me
              </button>
              <div style={{ height: 0.5, background: '#ececec', width: '100%', margin: '2px 0' }} />
              <button
                data-tour="lesson-ai-exercise"
                style={{ fontSize: 15, fontWeight: 400, color: '#222', background: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', paddingLeft: 18, cursor: 'pointer', width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', transition: 'background 0.22s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#ede2f9'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                onClick={() => { setShowTemplates(true); setShowAskMe(false); setSelectedTemplate(null); setExerciseResult(""); setShowQuizGenerator(false); setShowABCDGenerator(false); setShowOpenQuestionsGenerator(false); setShowWordDefinitionGenerator(false); setShowWordTranslationGenerator(false); setShowFillInTheGapsGenerator(false); setShowTrueFalseGenerator(false); setShowWarmUpDiscussionGenerator(false); setShowCreateDialogueGenerator(false); setShowEssentialVocabularyGenerator(false); setShowLeadInActivitiesGenerator(false); setShowFourOpinionsGenerator(false); setShowCommunicativeSituationsGenerator(false); setShowMatchingHalvesGenerator(false); setShowErrorCorrectionGenerator(false); setShowGrammarRulesGenerator(false); }}
              >
                {magicIcon} Generate an exercise
              </button>
              <button
                data-tour="lesson-ai-smart"
                style={{ fontSize: 15, fontWeight: 400, color: '#222', background: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', paddingLeft: 18, cursor: 'pointer', width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', transition: 'background 0.22s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#ede2f9'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                onClick={() => {
                  setShowSmartLesson(true);
                  setShowAskMe(false);
                  setShowTemplates(false);
                  setSelectedTemplate(null);
                  setExerciseResult("");
                  setShowQuizGenerator(false);
                  setShowABCDGenerator(false);
                  setShowOpenQuestionsGenerator(false);
                  setShowWordDefinitionGenerator(false);
                  setShowWordTranslationGenerator(false);
                  setShowFillInTheGapsGenerator(false);
                  setShowTrueFalseGenerator(false);
                  setShowWarmUpDiscussionGenerator(false);
                  setShowCreateDialogueGenerator(false);
                  setShowEssentialVocabularyGenerator(false);
                  setShowLeadInActivitiesGenerator(false);
                  setShowFourOpinionsGenerator(false);
                  setShowCommunicativeSituationsGenerator(false);
                  setShowMatchingHalvesGenerator(false);
                  setShowErrorCorrectionGenerator(false);
                  setShowGrammarRulesGenerator(false);
                }}
              >
                {smartLessonStarsIcon} Generate Smart Lesson
              </button>
            </>
          ) : null}

          {showSmartLesson && (
            <SmartLessonPanel
              editor={activeEditor || editor}
              onInsertToBoard={onInsertToBoard}
              onInsertSmartLesson={onInsertSmartLesson}
              onBack={() => setShowSmartLesson(false)}
              onClose={() => { setShowSmartLesson(false); setShowAIPopup(false); }}
            />
          )}

          {showAskMe && !showSmartLesson && !showTemplates && !showQuizGenerator && !showABCDGenerator && !showOpenQuestionsGenerator && !showWordDefinitionGenerator && !showWordTranslationGenerator && !showFillInTheGapsGenerator && !showTrueFalseGenerator && !showWarmUpDiscussionGenerator && !showCreateDialogueGenerator && !showEssentialVocabularyGenerator && !showLeadInActivitiesGenerator && !showFourOpinionsGenerator && !showCommunicativeSituationsGenerator && !showMatchingHalvesGenerator && !showErrorCorrectionGenerator && !showGrammarRulesGenerator && !showJumbledSentenceGenerator && !showMultipleChoiceGenerator && (
            <AskMePanel
              setShowAskMe={setShowAskMe} setAskMeInput={setAskMeInput} setAskMeResult={setAskMeResult}
              askMeMessages={askMeMessages} askMeLoading={askMeLoading}
              askMeCurrentMessage={askMeCurrentMessage} setAskMeCurrentMessage={setAskMeCurrentMessage}
              askMeMessagesEndRef={askMeMessagesEndRef}
              handleAskMeSendMessage={handleAskMeSendMessage} clearAskMeHistory={clearAskMeHistory}
            />
          )}

          <GeneratorPanelsContainer
            showQuizGenerator={showQuizGenerator} setShowQuizGenerator={setShowQuizGenerator}
            quizInput={quizInput} setQuizInput={setQuizInput} quizLoading={quizLoading} setQuizLoading={setQuizLoading}
            quizLevel={quizLevel} setQuizLevel={setQuizLevel} quizCount={quizCount} setQuizCount={setQuizCount}
            generatedQuiz={generatedQuiz} setGeneratedQuiz={setGeneratedQuiz} quizType={quizType} setQuizType={setQuizType}
            showABCDGenerator={showABCDGenerator} setShowABCDGenerator={setShowABCDGenerator}
            abcdInput={abcdInput} setABCDInput={setABCDInput} abcdLevel={abcdLevel} setABCDLevel={setABCDLevel}
            abcdLoading={abcdLoading} setABCDLoading={setABCDLoading} generatedABCD={generatedABCD} setGeneratedABCD={setGeneratedABCD}
            abcdQuestionCount={abcdQuestionCount} setABCDQuestionCount={setABCDQuestionCount}
            showOpenQuestionsGenerator={showOpenQuestionsGenerator} setShowOpenQuestionsGenerator={setShowOpenQuestionsGenerator}
            openQuestionsInput={openQuestionsInput} setOpenQuestionsInput={setOpenQuestionsInput}
            openQuestionsLevel={openQuestionsLevel} setOpenQuestionsLevel={setOpenQuestionsLevel}
            openQuestionsLoading={openQuestionsLoading} setOpenQuestionsLoading={setOpenQuestionsLoading}
            generatedOpenQuestions={generatedOpenQuestions} setGeneratedOpenQuestions={setGeneratedOpenQuestions}
            openQuestionsCount={openQuestionsCount} setOpenQuestionsCount={setOpenQuestionsCount}
            showWordDefinitionGenerator={showWordDefinitionGenerator} setShowWordDefinitionGenerator={setShowWordDefinitionGenerator}
            wordDefinitionInput={wordDefinitionInput} setWordDefinitionInput={setWordDefinitionInput}
            wordDefinitionLevel={wordDefinitionLevel} setWordDefinitionLevel={setWordDefinitionLevel}
            wordDefinitionLoading={wordDefinitionLoading} setWordDefinitionLoading={setWordDefinitionLoading}
            generatedWordDefinitions={generatedWordDefinitions} setGeneratedWordDefinitions={setGeneratedWordDefinitions}
            showWordTranslationGenerator={showWordTranslationGenerator} setShowWordTranslationGenerator={setShowWordTranslationGenerator}
            wordTranslationInput={wordTranslationInput} setWordTranslationInput={setWordTranslationInput}
            wordTranslationTargetLanguage={wordTranslationTargetLanguage} setWordTranslationTargetLanguage={setWordTranslationTargetLanguage}
            wordTranslationLoading={wordTranslationLoading} setWordTranslationLoading={setWordTranslationLoading}
            generatedWordTranslations={generatedWordTranslations} setGeneratedWordTranslations={setGeneratedWordTranslations}
            showFillInTheGapsGenerator={showFillInTheGapsGenerator} setShowFillInTheGapsGenerator={setShowFillInTheGapsGenerator}
            fillInTheGapsInput={fillInTheGapsInput} setFillInTheGapsInput={setFillInTheGapsInput}
            fillInTheGapsLevel={fillInTheGapsLevel} setFillInTheGapsLevel={setFillInTheGapsLevel}
            fillInTheGapsLoading={fillInTheGapsLoading} setFillInTheGapsLoading={setFillInTheGapsLoading}
            generatedFillInTheGaps={generatedFillInTheGaps} setGeneratedFillInTheGaps={setGeneratedFillInTheGaps}
            fillInTheGapsMode={fillInTheGapsMode} setFillInTheGapsMode={setFillInTheGapsMode}
            fillInTheGapsCustomText={fillInTheGapsCustomText} setFillInTheGapsCustomText={setFillInTheGapsCustomText}
            showTrueFalseGenerator={showTrueFalseGenerator} setShowTrueFalseGenerator={setShowTrueFalseGenerator}
            trueFalseInput={trueFalseInput} setTrueFalseInput={setTrueFalseInput}
            trueFalseLevel={trueFalseLevel} setTrueFalseLevel={setTrueFalseLevel}
            trueFalseCount={trueFalseCount} setTrueFalseCount={setTrueFalseCount}
            trueFalseLoading={trueFalseLoading} setTrueFalseLoading={setTrueFalseLoading}
            generatedTrueFalse={generatedTrueFalse} setGeneratedTrueFalse={setGeneratedTrueFalse}
            showWarmUpDiscussionGenerator={showWarmUpDiscussionGenerator} setShowWarmUpDiscussionGenerator={setShowWarmUpDiscussionGenerator}
            warmUpDiscussionInput={warmUpDiscussionInput} setWarmUpDiscussionInput={setWarmUpDiscussionInput}
            warmUpDiscussionLevel={warmUpDiscussionLevel} setWarmUpDiscussionLevel={setWarmUpDiscussionLevel}
            warmUpDiscussionCount={warmUpDiscussionCount} setWarmUpDiscussionCount={setWarmUpDiscussionCount}
            warmUpDiscussionLoading={warmUpDiscussionLoading} setWarmUpDiscussionLoading={setWarmUpDiscussionLoading}
            generatedWarmUpDiscussion={generatedWarmUpDiscussion} setGeneratedWarmUpDiscussion={setGeneratedWarmUpDiscussion}
            showCreateDialogueGenerator={showCreateDialogueGenerator} setShowCreateDialogueGenerator={setShowCreateDialogueGenerator}
            createDialogueInput={createDialogueInput} setCreateDialogueInput={setCreateDialogueInput}
            createDialogueLevel={createDialogueLevel} setCreateDialogueLevel={setCreateDialogueLevel}
            createDialogueSpeakers={createDialogueSpeakers} setCreateDialogueSpeakers={setCreateDialogueSpeakers}
            createDialogueLoading={createDialogueLoading} setCreateDialogueLoading={setCreateDialogueLoading}
            generatedCreateDialogue={generatedCreateDialogue} setGeneratedCreateDialogue={setGeneratedCreateDialogue}
            showEssentialVocabularyGenerator={showEssentialVocabularyGenerator} setShowEssentialVocabularyGenerator={setShowEssentialVocabularyGenerator}
            essentialVocabularyMode={essentialVocabularyMode} setEssentialVocabularyMode={setEssentialVocabularyMode}
            essentialVocabularyInput={essentialVocabularyInput} setEssentialVocabularyInput={setEssentialVocabularyInput}
            essentialVocabularyLevel={essentialVocabularyLevel} setEssentialVocabularyLevel={setEssentialVocabularyLevel}
            essentialVocabularyPartOfSpeech={essentialVocabularyPartOfSpeech} setEssentialVocabularyPartOfSpeech={setEssentialVocabularyPartOfSpeech}
            essentialVocabularyTranslationLanguage={essentialVocabularyTranslationLanguage} setEssentialVocabularyTranslationLanguage={setEssentialVocabularyTranslationLanguage}
            essentialVocabularyLoading={essentialVocabularyLoading} setEssentialVocabularyLoading={setEssentialVocabularyLoading}
            generatedEssentialVocabulary={generatedEssentialVocabulary} setGeneratedEssentialVocabulary={setGeneratedEssentialVocabulary}
            showLeadInActivitiesGenerator={showLeadInActivitiesGenerator} setShowLeadInActivitiesGenerator={setShowLeadInActivitiesGenerator}
            leadInActivitiesInput={leadInActivitiesInput} setLeadInActivitiesInput={setLeadInActivitiesInput}
            leadInActivitiesLevel={leadInActivitiesLevel} setLeadInActivitiesLevel={setLeadInActivitiesLevel}
            leadInActivitiesLoading={leadInActivitiesLoading} setLeadInActivitiesLoading={setLeadInActivitiesLoading}
            generatedLeadInActivities={generatedLeadInActivities} setGeneratedLeadInActivities={setGeneratedLeadInActivities}
            showFourOpinionsGenerator={showFourOpinionsGenerator} setShowFourOpinionsGenerator={setShowFourOpinionsGenerator}
            fourOpinionsInput={fourOpinionsInput} setFourOpinionsInput={setFourOpinionsInput}
            fourOpinionsLevel={fourOpinionsLevel} setFourOpinionsLevel={setFourOpinionsLevel}
            fourOpinionsCount={fourOpinionsCount} setFourOpinionsCount={setFourOpinionsCount}
            fourOpinionsLoading={fourOpinionsLoading} setFourOpinionsLoading={setFourOpinionsLoading}
            generatedFourOpinions={generatedFourOpinions} setGeneratedFourOpinions={setGeneratedFourOpinions}
            showCommunicativeSituationsGenerator={showCommunicativeSituationsGenerator} setShowCommunicativeSituationsGenerator={setShowCommunicativeSituationsGenerator}
            communicativeSituationsVocab={communicativeSituationsVocab} setCommunicativeSituationsVocab={setCommunicativeSituationsVocab}
            communicativeSituationsTopic={communicativeSituationsTopic} setCommunicativeSituationsTopic={setCommunicativeSituationsTopic}
            communicativeSituationsLevel={communicativeSituationsLevel} setCommunicativeSituationsLevel={setCommunicativeSituationsLevel}
            communicativeSituationsLoading={communicativeSituationsLoading} setCommunicativeSituationsLoading={setCommunicativeSituationsLoading}
            generatedCommunicativeSituations={generatedCommunicativeSituations} setGeneratedCommunicativeSituations={setGeneratedCommunicativeSituations}
            showMatchingHalvesGenerator={showMatchingHalvesGenerator} setShowMatchingHalvesGenerator={setShowMatchingHalvesGenerator}
            matchingHalvesInput={matchingHalvesInput} setMatchingHalvesInput={setMatchingHalvesInput}
            matchingHalvesLevel={matchingHalvesLevel} setMatchingHalvesLevel={setMatchingHalvesLevel}
            matchingHalvesMode={matchingHalvesMode} setMatchingHalvesMode={setMatchingHalvesMode}
            matchingHalvesLoading={matchingHalvesLoading} setMatchingHalvesLoading={setMatchingHalvesLoading}
            generatedMatchingHalves={generatedMatchingHalves} setGeneratedMatchingHalves={setGeneratedMatchingHalves}
            showErrorCorrectionGenerator={showErrorCorrectionGenerator} setShowErrorCorrectionGenerator={setShowErrorCorrectionGenerator}
            errorCorrectionInput={errorCorrectionInput} setErrorCorrectionInput={setErrorCorrectionInput}
            errorCorrectionLevel={errorCorrectionLevel} setErrorCorrectionLevel={setErrorCorrectionLevel}
            errorCorrectionSentenceCount={errorCorrectionSentenceCount} setErrorCorrectionSentenceCount={setErrorCorrectionSentenceCount}
            errorCorrectionLoading={errorCorrectionLoading} setErrorCorrectionLoading={setErrorCorrectionLoading}
            generatedErrorCorrection={generatedErrorCorrection} setGeneratedErrorCorrection={setGeneratedErrorCorrection}
            showGrammarRulesGenerator={showGrammarRulesGenerator} setShowGrammarRulesGenerator={setShowGrammarRulesGenerator}
            grammarRulesInput={grammarRulesInput} setGrammarRulesInput={setGrammarRulesInput}
            grammarRulesLevel={grammarRulesLevel} setGrammarRulesLevel={setGrammarRulesLevel}
            grammarRulesLanguage={grammarRulesLanguage} setGrammarRulesLanguage={setGrammarRulesLanguage}
            grammarRulesLoading={grammarRulesLoading} setGrammarRulesLoading={setGrammarRulesLoading}
            generatedGrammarRules={generatedGrammarRules} setGeneratedGrammarRules={setGeneratedGrammarRules}
            showProsConsGenerator={showProsConsGenerator} setShowProsConsGenerator={setShowProsConsGenerator}
            prosConsInput={prosConsInput} setProsConsInput={setProsConsInput}
            prosConsLevel={prosConsLevel} setProsConsLevel={setProsConsLevel}
            prosConsPairs={prosConsPairs} setProsConsPairs={setProsConsPairs}
            prosConsLoading={prosConsLoading} setProsConsLoading={setProsConsLoading}
            generatedProsCons={generatedProsCons} setGeneratedProsCons={setGeneratedProsCons}
            showJumbledSentenceGenerator={showJumbledSentenceGenerator} setShowJumbledSentenceGenerator={setShowJumbledSentenceGenerator}
            jumbledSentenceInput={jumbledSentenceInput} setJumbledSentenceInput={setJumbledSentenceInput}
            jumbledSentenceLevel={jumbledSentenceLevel} setJumbledSentenceLevel={setJumbledSentenceLevel}
            jumbledSentenceCount={jumbledSentenceCount} setJumbledSentenceCount={setJumbledSentenceCount}
            jumbledSentenceLoading={jumbledSentenceLoading} setJumbledSentenceLoading={setJumbledSentenceLoading}
            generatedJumbledSentence={generatedJumbledSentence} setGeneratedJumbledSentence={setGeneratedJumbledSentence}
            jumbledSentenceMode={jumbledSentenceMode} setJumbledSentenceMode={setJumbledSentenceMode}
            showMultipleChoiceGenerator={showMultipleChoiceGenerator} setShowMultipleChoiceGenerator={setShowMultipleChoiceGenerator}
            multipleChoiceInput={multipleChoiceInput} setMultipleChoiceInput={setMultipleChoiceInput}
            multipleChoiceLevel={multipleChoiceLevel} setMultipleChoiceLevel={setMultipleChoiceLevel}
            multipleChoiceMode={multipleChoiceMode} setMultipleChoiceMode={setMultipleChoiceMode}
            multipleChoiceCount={multipleChoiceCount} setMultipleChoiceCount={setMultipleChoiceCount}
            multipleChoiceOptionCount={multipleChoiceOptionCount} setMultipleChoiceOptionCount={setMultipleChoiceOptionCount}
            multipleChoiceLoading={multipleChoiceLoading} setMultipleChoiceLoading={setMultipleChoiceLoading}
            generatedMultipleChoice={generatedMultipleChoice} setGeneratedMultipleChoice={setGeneratedMultipleChoice}
            showTemplates={showTemplates} setShowTemplates={setShowTemplates}
            templateTagFilter={templateTagFilter} setTemplateTagFilter={setTemplateTagFilter}
            selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate}
            exerciseResult={exerciseResult} setExerciseResult={setExerciseResult}
            showAskMe={showAskMe} setShowAskMe={setShowAskMe} showSmartLesson={showSmartLesson}
            onInsertToBoard={onInsertToBoard} currentTab={currentTab}
            editor={editor}
            handleTemplateSelect={handleTemplateSelect} TEMPLATES={TEMPLATES}
          />

          <style>{`
            @keyframes fadeInAI {
              from { opacity: 0; transform: translateY(20px) scale(0.95); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
            
            /* Стили для скроллбара в WebKit браузерах */
            .templates-scroll::-webkit-scrollbar {
              width: 4px;
            }
            
            .templates-scroll::-webkit-scrollbar-track {
              background: transparent;
              border-radius: 2px;
            }
            
            .templates-scroll::-webkit-scrollbar-thumb {
              background: rgba(156, 163, 175, 0.3);
              border-radius: 2px;
            }
            
            .templates-scroll::-webkit-scrollbar-thumb:hover {
              background: rgba(156, 163, 175, 0.5);
            }
            
            /* Hide scrollbar for tags section */
            .tags-scroll::-webkit-scrollbar {
              display: none;
            }
          `}</style>
        </div>
      )}
      {showAI && (
        <AIStarAssistantModal
          onClose={() => setShowAI(false)}
          editor={activeEditor}
          onInsertToBoard={onInsertToBoard}
          currentTab={currentTab}
        />
      )}
      {show && (
        <div ref={menuRef} style={{
          position: "absolute",
          top: coords.top,
          left: coords.left,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 4px 24px #8a2be244",
          padding: 12,
          minWidth: 280,
          maxWidth: 280,
          width: 280,
          border: "1.5px solid #cfcfff",
          zIndex: 2147483647,
          overflow: "hidden",
          boxSizing: "border-box",
          animation: 'fadeInAI 0.28s cubic-bezier(.4,0,.2,1)'
        }}>
          {showTablePicker ? (
            <TablePicker
              onPick={(rows, cols) => {
                if (currentTab > 0 && onInsertToBoard) {
                  onInsertToBoard({ rows, cols }, 'grid');
                } else if (activeEditor) {
                  activeEditor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
                }
                setShow(false);
                setShowTablePicker(false);
                setSearch("");
                setSelected(0);
              }}
              onBack={() => setShowTablePicker(false)}
            />
          ) : showUnsplash ? (
            <div style={{ minWidth: 260 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, height: 36 }}>
                <button
                  style={{ marginRight: 2, padding: '4px 8px', borderRadius: 8, border: 'none', background: '#f3eafd', color: '#8a2be2', fontWeight: 600, cursor: 'pointer', fontSize: 16, minWidth: 0, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={() => setShowUnsplash(false)}
                  title="Back to menu"
                >←</button>
                <input
                  value={unsplashQuery}
                  onChange={e => setUnsplashQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') searchUnsplash(unsplashQuery); }}
                  placeholder="Search Unsplash..."
                  style={{ flex: 1, minWidth: 0, maxWidth: 180, padding: '6px 8px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: 15, outline: 'none', height: 32, boxSizing: 'border-box', display: 'flex', alignItems: 'center' }}
                />
                <button
                  style={{ marginLeft: 4, padding: '4px 10px', borderRadius: 8, border: 'none', background: '#ede2f9', color: '#8a2be2', fontWeight: 600, cursor: 'pointer', fontSize: 16, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={() => searchUnsplash(unsplashQuery)}
                  title="Search"
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="7" stroke="#8a2be2" strokeWidth="2"/><path d="M15 15L18 18" stroke="#8a2be2" strokeWidth="2" strokeLinecap="round"/></svg>
                </button>
              </div>
              {unsplashLoading && <div style={{ color: '#aaa', padding: 8 }}>Loading...</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {unsplashResults.map(img => (
                  <img
                    key={img.id}
                    src={img.urls.thumb}
                    alt={img.alt_description || ''}
                    style={{ width: '100%', borderRadius: 8, cursor: 'pointer', boxShadow: '0 1px 4px #8a2be211' }}
                    onClick={() => {
                      if (currentTab > 0 && onInsertToBoard) {
                        onInsertToBoard({ type: 'file', value: img.urls.regular, name: img.alt_description || 'Unsplash Image', extension: 'jpg' }, 'material');
                      } else if (activeEditor) {
                        activeEditor.chain().focus().setImage({
                          src: img.urls.regular,
                          width: '400px',
                          height: 'auto',
                          align: 'center',
                          draggable: true,
                          class: 'resizable-image',
                          imageId: `img_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`
                        }).run();
                      }
                      setShowUnsplash(false);
                      setShow(false);
                      setSearch("");
                      setSelected(0);
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <>
              <input
                ref={inputRef}
                value={search}
                onChange={e => { setSearch(e.target.value); setSelected(0); }}
                placeholder="Search actions"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1.5px solid #e0e0e0",
                  marginBottom: 10,
                  fontSize: 16,
                  outline: "none",
                  boxSizing: "border-box"
                }}
              />
              <div style={{ maxHeight: 180, overflowY: "auto" }}
              onWheel={handleScrollInDropdown}>
                {filteredOptions.length === 0 && <div style={{ color: "#aaa", padding: 8 }}>No results</div>}
                {filteredOptions.map((opt, i) => (
                  <div
                    key={opt.label}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: 16,
                      marginBottom: 2,
                      background: i === selected ? "#ede2f9" : "none",
                      color: i === selected ? "#8a2be2" : "#000",
                      display: 'flex', alignItems: 'center', gap: 10
                    }}
                    onClick={() => handleOptionClick(opt, i)}
                    onMouseDown={e => e.preventDefault()}
                    onMouseEnter={() => setSelected(i)}
                  >
                    <span style={{fontSize: 20, display: 'flex', alignItems: 'center'}}>{opt.icon}</span> {opt.label}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      <style>{`
        @keyframes fadeInAI {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      
      {/* PDF Page Selector Modal */}
      {pdfModal && (
        <PDFPageSelectorModal
          pdfUrl={pdfModal.url}
          onClose={handlePdfModalClose}
          onInsertPages={handlePdfPagesInsert}
        />
      )}
      
      {/* Materials Selector Modal */}
      <MaterialsSelectorModal
        isOpen={materialsSelectorModal}
        onClose={() => setMaterialsSelectorModal(false)}
        onSelectMaterial={(material) => {
          // Если мы на доске (currentTab > 0) и есть onInsertToBoard
          if (currentTab > 0 && onInsertToBoard) {
            onInsertToBoard(material, 'material');
          } else if (currentTab === 0 && activeEditor) {
            // Если мы в редакторе (currentTab === 0) и есть activeEditor
            handleMaterialSelect(material);
          }
          
          // Закрываем модальное окно после выбора материала
          setMaterialsSelectorModal(false);
        }}
        activeEditor={activeEditor}
        setPdfModal={setPdfModal}
      />
    </div>
  );
}
